import { zodResolver } from "@hookform/resolvers/zod";
import { debounce } from "@tanstack/react-pacer";
import type { ComponentProps } from "react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";

import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from "../form";
import { Input } from "../input";
import {
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  Pagination as PaginationRoot,
} from "../pagination";
import { Spinner } from "../spinner";
import { cn, selectAllText } from "../utils";

import { DEFAULT_MAXIMUM_PAGE_SIZE } from "./consts";

import { Tk, useTranslation2 } from "~/shared/i18n";
import { DEBOUNCE_TIME } from "~/shared/pacer";
import { COLON_WITH_SPACE } from "~/shared/str";
import { Pagination } from "~/shared/zod";
import { getLastPageNumber } from "~/shared/zod/pagination";

type Props = ComponentProps<"div"> &
  Pick<Pagination.Meta.Schema, "totalItems"> & {
    options: Pagination.Options.Schema;
    onOptionsChange?: ((value: Pagination.Options.Schema) => void) | undefined;
    showSpinner?: boolean | undefined;
    maximumPageSize?: number | undefined;
  };

function PaginationControl({
  totalItems,
  options,
  onOptionsChange,
  showSpinner,
  maximumPageSize = DEFAULT_MAXIMUM_PAGE_SIZE,
  className,
  ...props
}: Props) {
  const {
    postproc: { tc },
  } = useTranslation2();

  const meta = Pagination.Meta.zSchema.parse({
    options,
    totalItems,
  } satisfies z.input<typeof Pagination.Meta.zSchema>);

  const optionsForm = useForm({
    resolver: zodResolver(Pagination.Options.zSchema.removeDefault()),
    defaultValues: options,
  });

  const changePageNumber = (number: number) => {
    if (number !== meta.number) {
      onOptionsChange?.({
        number,
        size: meta.size,
      });
    }
  };

  const changePageSize = (size: number) => {
    if (size !== meta.size) {
      const newOptions = {
        number: meta.number,
        size,
      };
      const lastPageNumber = getLastPageNumber({
        size: newOptions.size,
        totalItems: meta.totalItems,
      });
      if (newOptions.number > lastPageNumber) {
        newOptions.number = lastPageNumber;
      }
      onOptionsChange?.(newOptions);
    }
  };

  const debouncePageNumber = debounce((value: number) => changePageNumber(value), {
    wait: DEBOUNCE_TIME,
  });

  const debouncePageSize = debounce((value: number) => changePageSize(value), {
    wait: DEBOUNCE_TIME,
  });

  useEffect(() => {
    const subscription = optionsForm.watch(({ number, size }) => {
      if (typeof number === "number" && number !== meta.number) {
        debouncePageNumber(number);
      }
      if (typeof size === "number" && size !== meta.size) {
        debouncePageSize(size);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [debouncePageNumber, debouncePageSize, meta.number, meta.size, optionsForm]);

  useEffect(() => {
    const { number, size } = meta;
    if (number !== optionsForm.getValues().number) {
      optionsForm.setValue("number", number);
    }
    if (size !== optionsForm.getValues().size) {
      optionsForm.setValue("size", size);
    }
  }, [meta, optionsForm]);

  return (
    <div className={cn("flex flex-wrap justify-end gap-2", className)} {...props}>
      <div className="flex items-center justify-end gap-2">
        <Spinner className={cn(!showSpinner && "invisible")} />
        <Form {...optionsForm}>
          <form className="flex gap-2">
            {(
              [
                {
                  name: "number",
                  labelTk: Tk.NUMBER,
                  max: meta.totalPages,
                },
                {
                  name: "size",
                  labelTk: Tk.SIZE,
                  max: maximumPageSize,
                },
              ] satisfies ({
                name: keyof Pagination.Options.Schema;
              } & Record<string, unknown>)[]
            ).map(({ name, labelTk, max }) => (
              <FormField
                key={name}
                control={optionsForm.control}
                name={name}
                render={({ field: { onChange, ...field } }) => {
                  const MINIMUM_VALUE = 1;
                  return (
                    <FormItem className="grid-cols-2 gap-y-0">
                      <FormLabel>{tc(labelTk)}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step={1}
                          min={MINIMUM_VALUE}
                          max={max}
                          className="w-16"
                          onChange={(event) => {
                            const value = event.target.valueAsNumber;
                            if (isNaN(value) || value < MINIMUM_VALUE || (max && value > max))
                              return;

                            onChange(value);
                          }}
                          onFocus={selectAllText}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription className="col-start-2">
                        {[tc(Tk.MAX), max].join(COLON_WITH_SPACE)}
                      </FormDescription>
                    </FormItem>
                  );
                }}
              />
            ))}
          </form>
        </Form>
      </div>
      <PaginationRoot
        total={meta.totalItems}
        itemsPerPage={meta.size}
        pageCount={meta.totalPages}
        currentPage={meta.number}
        onPageChange={changePageNumber}
      >
        <PaginationContent
          className="justify-end"
          render={(pages, activePage) =>
            pages.map((page, index) => (
              <PaginationItem key={index}>
                {page.type === "page" ? (
                  <PaginationLink page={page.value} isActive={page.value === activePage} />
                ) : (
                  <PaginationEllipsis />
                )}
              </PaginationItem>
            ))
          }
        />
      </PaginationRoot>
    </div>
  );
}

export { PaginationControl };
